/* eslint-disable max-statements */
import {financingChoices, infraestructureChoices, statusChoices} from 'constants/fieldGroups';

import moment from 'moment';
import {camelCase} from 'lodash';
import snakecase from 'lodash.snakecase';

import {RESOURCES} from '~constants/config';
import {DATE_TIME_FORMAT, TIME_FORMAT} from '~constants/dates';
import {snakecaseObject} from '~utils/snakecaseObject';
import {parsePosition, positionKey} from '~utils/trackingPosition';
import {Document, Image, Link} from '~components/GalleryItemsInput/classes';

export const camelizeKeys = obj => {
    if (Array.isArray(obj)) {
        return obj.map(v => camelizeKeys(v));
    } else if (obj !== null && obj.constructor === Object) {
        return Object.keys(obj).reduce(
            (result, key) => ({
                ...result,
                [camelCase(key)]: camelizeKeys(obj[key])
            }),
            {}
        );
    }
    return obj;
};

export const parseResponse = rawResponse => ({
    data: rawResponse.data.page,
    total: rawResponse.data.count
});


const parseTime = date => moment.utc(date).format(TIME_FORMAT);

const formatDateTime = (date, time) =>
    moment.utc(`${date} ${time ? time : ''}`, DATE_TIME_FORMAT).toISOString();

// The form opens with today's date as the new "last update": saving the Seguimiento
// is what updates it. The stored date stays visible as `recordedLastUpdate`.
// The position (current etapa + sub-etapa) is edited as one `position` key; per-etapa
// progress is derived and read-only, so only dates go back per etapa.
export const parseProjectTracking = (data, code) => ({
    ...data,
    id: code,
    position: data.currentStage ? positionKey(data.currentStage, data.currentSubStage) : null,
    recordedLastUpdate: data.lastUpdate,
    lastUpdate: moment().format('YYYY-MM-DD')
});

export const formatProjectTracking = data => ({
    lastUpdate: data.lastUpdate || undefined,
    description: data.description || '',
    ...parsePosition(data.position),
    currentProgress: Number(data.currentProgress) || 0,
    stages: (data.stages || []).map(stage => ({
        id: stage.id,
        startDate: stage.startDate || null,
        expectedEndDate: stage.expectedEndDate || null
    }))
});

export const formatTopicCreate = data => ({
    title: data.title
});

const formatDate = date => {
    const formattedDate = date.split('T')[0];
    return moment.utc(formattedDate, 'YYYY-MM-DD').format('DD-MM-YYYY');
};

export const parseProject = data => ({
    ...data,
    dynamic_map_lat: getDynamicCenter(data) ? getDynamicCenter(data).latitude : null,
    dynamic_map_lng: getDynamicCenter(data) ? getDynamicCenter(data).longitude : null,
    dynamic_map_zoom: data.dynamic_map_zoom ? data.dynamic_map_zoom : data.dynamicMapZoom,
    static_map_lat: getStaticCenter(data) ? getStaticCenter(data).latitude : null,
    static_map_lng: getStaticCenter(data) ? getStaticCenter(data).longitude : null,
    static_map_zoom: data.static_map_zoom ? data.static_map_zoom : data.staticMapZoom,
    estimated_end_date: data.estimatedEndDate
        ? formatDate(data.estimatedEndDate)
        : data.estimated_end_date
            ? formatDate(data.estimated_end_date)
            : '',
    project_countries: data.projectCountries
        ? data.projectCountries
        : data.project_countries
            ? data.project_countries
            : [],
    image_name: data.imageName ? data.imageName : data.image_name,
    owners: data.owners ? parseOwners(data.owners) : [],
    ambiental_license: data.ambientalLicense ? data.ambientalLicense : data.ambiental_license,
    project_gallery_items: data.projectGalleryItems
        ? data.projectGalleryItems
        : data.project_gallery_items
            ? data.project_gallery_items
            : null
});

export const parseIirsaProject = data => {
    const snakecasedata = snakecaseObject(data);
    const dynamic_center = getDynamicCenter(snakecasedata);
    const static_center = getStaticCenter(snakecasedata);
    const retval = {
        ...snakecasedata,
        id: snakecasedata.code,
        is_portfolio: !!snakecasedata.is_portfolio,
        is_bid: !!snakecasedata.is_bid,
        is_caf: !!snakecasedata.is_caf,
        is_fonplata: !!snakecasedata.is_fonplata,
        accompaniment: !!snakecasedata.accompaniment,
        is_anchor: !!snakecasedata.is_anchor,
        has_ambiental_license: !!snakecasedata.has_ambiental_license,
        iirsa_status: snakecasedata.iirsa_status?.id,
        iirsa_stage: snakecasedata.iirsa_stage?.id,
        iirsa_countries: snakecasedata.iirsa_countries ? snakecasedata.iirsa_countries.map(c => c.id) : [],
        iirsa_sector: snakecasedata.iirsa_sector?.id,
        iirsa_subsector: snakecasedata.iirsa_subsector?.id,
        iirsa_project_type: snakecasedata.iirsa_project_type?.id,
        iirsa_scope: snakecasedata.iirsa_scope?.id,
        iirsa_studies_state: snakecasedata.iirsa_studies_state?.id,
        iirsa_studies_funder: snakecasedata.iirsa_studies_funder?.id,
        iirsa_project_funding: snakecasedata.iirsa_project_funding.map(e => ({
            amount: e.amount,
            funder: e.funder?.id,
            funding_status: e.funding_status?.id,
            funding_type: e.funding_type?.id
        })),
        iirsa_segments: snakecasedata.iirsa_segments ? snakecasedata.iirsa_segments.map(({iirsa_stage, ...e}) => {
            e.iirsa_stage = iirsa_stage?.id || iirsa_stage;
            return e;
        }) : [],
        dynamic_center_latitude: dynamic_center ? dynamic_center.latitude : null,
        dynamic_center_longitude: dynamic_center ? dynamic_center.longitude : null,
        dynamic_map_zoom: dynamic_center ? dynamic_center.zoom : null,
        static_center_latitude: static_center ? static_center.latitude : null,
        static_center_longitude: static_center ? static_center.longitude : null,
        static_map_zoom: static_center ? static_center.zoom : null,
        route: snakecasedata?.route?.id,
        prioritization: snakecasedata.prioritization?.id,
    };
    return retval;
};

// Drafts may be incomplete and store nested children as ids/dicts in `children`,
// surfaced flat by the draft read serializer. Tolerant of missing fields; id = uuid.
export const parseDraftProject = data => {
    const d = snakecaseObject(data);
    const dynamic_center = d.dynamic_center || null;
    const static_center = d.static_center || null;
    const fkId = value => (value && typeof value === 'object' ? value.id : value);
    return {
        ...d,
        id: d.uuid,
        is_portfolio: !!d.is_portfolio,
        is_bid: !!d.is_bid,
        is_caf: !!d.is_caf,
        is_fonplata: !!d.is_fonplata,
        accompaniment: !!d.accompaniment,
        is_anchor: !!d.is_anchor,
        has_ambiental_license: !!d.has_ambiental_license,
        // Draft children store FK ids directly (not nested objects).
        iirsa_status: fkId(d.iirsa_status),
        iirsa_stage: fkId(d.iirsa_stage),
        iirsa_countries: Array.isArray(d.iirsa_countries) ? d.iirsa_countries.map(fkId) : [],
        iirsa_sector: fkId(d.iirsa_sector),
        iirsa_subsector: fkId(d.iirsa_subsector),
        iirsa_project_type: fkId(d.iirsa_project_type),
        iirsa_scope: fkId(d.iirsa_scope),
        iirsa_studies_state: fkId(d.iirsa_studies_state),
        iirsa_studies_funder: fkId(d.iirsa_studies_funder),
        iirsa_project_funding: (d.iirsa_project_funding || []).map(e => ({
            amount: e.amount !== undefined ? e.amount : e.monto,
            funder: fkId(e.funder !== undefined ? e.funder : e.financiador_estudios),
            funding_status: fkId(e.funding_status !== undefined ? e.funding_status : e.estado_financiamiento),
            funding_type: fkId(e.funding_type !== undefined ? e.funding_type : e.tipo_financiamiento)
        })),
        iirsa_segments: (d.iirsa_segments || []).map(({iirsa_stage, etapa, ...e}) => ({
            ...e,
            iirsa_stage: fkId(iirsa_stage !== undefined ? iirsa_stage : etapa)
        })),
        dynamic_center_latitude: dynamic_center ? dynamic_center.latitude : null,
        dynamic_center_longitude: dynamic_center ? dynamic_center.longitude : null,
        dynamic_map_zoom: dynamic_center ? dynamic_center.zoom : null,
        static_center_latitude: static_center ? static_center.latitude : null,
        static_center_longitude: static_center ? static_center.longitude : null,
        static_map_zoom: static_center ? static_center.zoom : null,
        route: fkId(d.route),
        prioritization: fkId(d.prioritization),
    };
};

// List response: drafts already carry flat fields; just key by uuid.
export const formatDraftProjectResponse = data =>
    data.map(item => ({...item, id: item.uuid || item.code}));

const parseOwners = owners => {
    owners.forEach(o => {
        (o['first_name'] = o.firstName ? o.firstName : o.first_name),
            (o['last_name'] = o.lastName ? o.lastName : o.last_name);
    });
    return owners;
};

const getDynamicCenter = data => {
    if (data.kml) {
        return data.dynamic_center ? data.dynamic_center : data.dynamicCenter;
    }
    return null;
};

const getStaticCenter = data => {
    if (data.kml_simplified) {
        return data.static_center ? data.static_center : data.staticCenter;
    }
    return null;
};

export const formatCreateProject = data => ({
    title: data.title,
    infrastructure: data.infrastructure,
    objectives: data.objectives,
    status: data.status,
    cities: data.cities,
    area: data.area,
    kml: data.kml,
    kml_simplified: data.kml_simplified,
    estimated_end_date: data.estimated_end_date ? formatDate(data.estimated_end_date) : '',
    slug: data.slug
});

export const formatCreateRoute = data => data;

export const formatCreateIirsaProject = data => ({
    title: data.title,
    // infrastructure: data.infrastructure,
    // objectives: data.objectives,
    // status: data.status,
    cities: data.cities
    // area: data.area,
    // kml: data.kml,
    // kml_simplified: data.kml_simplified,
    // estimated_end_date: data.estimated_end_date ? formatDate(data.estimated_end_date) : '',
    // slug: data.slug
});

export const formatUpdateProject = data => ({
    ...formatCreateProject(data),
    id: data.id
});

export const formatProjectFormData = data => {
    let links = [];
    if (data.gallery_items) {
        links = getLinks(data.gallery_items);
        data.gallery_items = formatGalleryItems(data.gallery_items);
    } else {
        data.gallery_items = [];
    }
    return {
        infrastructure: data.infrastructure,
        title: data.title,
        objectives: data.objectives,
        status: data.status,
        cities: data.cities,
        area: data.area,
        dynamic_map_lat: data.dynamic_map_lat,
        dynamic_map_lng: data.dynamic_map_lng,
        dynamic_map_zoom: data.dynamic_map_zoom,
        static_map_lat: data.static_map_lat,
        static_map_lng: data.static_map_lng,
        static_map_zoom: data.static_map_zoom,
        subsector: data.subsector,
        project_type: data.project_type,
        content: data.content,
        ambiental_license: data.ambiental_license,
        financing: data.financing,
        estimated_end_date: data.estimated_end_date,
        projects: data.projects,
        project_countries: data.project_countries,
        bank: data.bank,
        links: JSON.stringify(links),
        sources: data.sources ? JSON.stringify(data.sources) : null,
        owners: data.owners ? JSON.stringify(data.owners) : null,
        kml: data.kml?.rawFile ? data.kml.rawFile : data.kml,
        kml_simplified: data.kml_simplified?.rawFile ? data.kml_simplified.rawFile : data.kml_simplified,
        image: data.image?.rawFile,
        image_name: data.image?.rawFile?.name,
        situation: data.situation,
        sections: data.sections ? JSON.stringify(data.sections) : null,
        url: data.url ? data.url : '',
        slug: data.slug ? data.slug : ''
    };
};

// eslint-disable-next-line complexity
export const formatIirsaProjectFormData = data => {
    let links = [];
    if (data.gallery_items) {
        data.project_gallery_items = formatGalleryItems(data.gallery_items);
        links = getLinks(data.project_gallery_items);
    } else {
        data.project_gallery_items = [];
    }
    const request = {
        is_portfolio: !!data.is_portfolio,
        is_bid: !!data.is_bid,
        is_caf: !!data.is_caf,
        is_fonplata: !!data.is_fonplata,
        accompaniment: !!data.accompaniment,
        is_anchor: !!data.is_anchor,
        has_ambiental_license: !!data.has_ambiental_license,
        studies_finished: !!data.studies_finished,
        code: data.code,
        name: data.name,
        iirsa_sector: data.iirsa_sector,
        iirsa_subsector: data.iirsa_subsector,
        goal: data.goal,
        description: data.description,
        iirsa_stage: data.iirsa_stage,
        iirsa_countries: data.iirsa_countries,
        iirsa_project_type: data.iirsa_project_type,
        iirsa_scope: data.iirsa_scope,
        iirsa_status: data.iirsa_status,
        status_comments: data.status_comments,
        studies_amount: data.studies_amount,
        studies_status_comments: data.studies_status_comments,
        studies_funding_comments: data.studies_funding_comments,
        iirsa_studies_state: data.iirsa_studies_state,
        iirsa_studies_funder: data.iirsa_studies_funder,
        funding_comments: data.funding_comments,
        kml: data.kml?.rawFile ? data.kml.rawFile : null,
        kml_simplified: data.kml_simplified?.rawFile ? data.kml_simplified.rawFile : null,
        kml_name: data.kml?.rawFile ? data.kml.rawFile.name : data.kml,
        kml_simplified_name: data.kml_simplified?.rawFile ? data.kml_simplified.rawFile.name : data.kml_simplified,
        dynamic_center: data.kml ? {
            latitude: data.dynamic_center_latitude,
            longitude: data.dynamic_center_longitude,
            zoom: data.dynamic_map_zoom
        } : null,
        static_center: data.kml_simplified ? {
            latitude: data.static_center_latitude,
            longitude: data.static_center_longitude,
            zoom: data.static_map_zoom
        } : null,
        iirsa_segments: data.iirsa_segments,
        iirsa_project_funding: data.iirsa_project_funding,
        links: JSON.stringify(links),
        gallery_items: data.project_gallery_items,
        end_date: data.end_date ? `${data.end_date}-01` : data.end_date,
        prioritization: data.prioritization,
        route: data.route
    };
    for (const key in request) {
        if (request[key] === null || request[key] === undefined) {
            delete request[key];
        }
    }
    return request;
};

export const formatRouteFormData = data => {
    return {
        ...data,
        kmz: data.kmz?.rawFile ? data.kmz.rawFile : undefined,
        ajuste_mapa: data.kmz ? {
            latitude: data.dynamic_center_latitude,
            longitude: data.dynamic_center_longitude,
            zoom: data.dynamic_map_zoom
        } : null,
    };
};

export const formatLayerFormData = data => ({
    name: data.name,
    files: (data.files || []).map(f => f.rawFile).filter(Boolean),
});

// LayerForm nests publishing fields under `publishing` so redux-form can seed
// them from the record's own nested `publishing` object. Nothing is sent to
// the API unless a config already exists (publishing.id) or the operator just
// checked "Publicar en GeoServer" (publishing.enabled) -- otherwise a plain
// layer-name edit would accidentally create an invalid, empty publishing row.
export const formatLayerUpdateFormData = data => {
    const publishing = data.publishing;
    if (!publishing || (!publishing.id && !publishing.enabled)) {
        return {};
    }
    return {
        publishedName: publishing.publishedName,
        title: publishing.title,
        titleEn: publishing.titleEn,
        titlePt: publishing.titlePt,
        workspace: publishing.workspace,
        store: publishing.store,
        srs: publishing.srs,
        sld: publishing.sld,
        sldFile: (publishing.sldFile && publishing.sldFile.rawFile) || undefined,
        enabled: !!publishing.enabled,
    };
};

const formatGalleryItems = galleryItems =>
    galleryItems.map(gi => {
        let newItem = null;
        switch (gi.type) {
            case 'Document':
                newItem = new Document();
                newItem.populate(gi);
                if (typeof gi.file === 'string') {
                    newItem.file = null;
                }
                break;
            case 'Image':
                newItem = new Image();
                newItem.populate(gi);
                if (typeof gi.file === 'string') {
                    newItem.file = null;
                }
                break;
            case 'Link':
                newItem = new Link();
                newItem.populate(gi);
                break;
            default:
                newItem = {...gi};
                break;
        }
        return newItem;
    });

const getLinks = galleryItems => {
    const links = new Array();
    galleryItems.forEach(gi => {
        if (gi instanceof Link) {
            links.push(gi);
        }
    });
    return links;
};

export const formatProjectResponse = data =>
    data.map(item => {
        return {
            ...item,
            status: statusChoices.filter(c => c.id == item.status)[0].name,
            infrastructure: infraestructureChoices.filter(c => c.id == item.infrastructure)[0].name
        };
    });

export const formatIirsaProjectResponse = data =>
    data.map(item => {
        return {
            ...item,
            id: item.code
            // status: statusChoices.filter(c => c.id == item.status)[0].name,
            // infrastructure: infraestructureChoices.filter(c => c.id == item.infrastructure)[0].name
        };
    });


export const formatRouteResponse = data => data.map(item => {
    return {
        ...item,
        dynamic_center_latitude: item.ajusteMapa?.latitude || 1,
        dynamic_center_longitude: item.ajusteMapa?.longitude || 1,
        dynamic_map_zoom: item.ajusteMapa?.zoom || 1
    }
});

export const formatShowProject = data => ({
    ...data,
    status: statusChoices.filter(c => c.id == data.status)[0].name,
    infrastructure: infraestructureChoices.filter(c => c.id == data.infrastructure)[0].name
});

export const formatShowIirsaProject = data => {
    const parsedData = {
        is_portfolio: !!data.isPortfolio,
        is_bid: !!data.isBid,
        is_caf: !!data.isCaf,
        is_fonplata: !!data.isFonplata,
        accompaniment: !!data.accompaniment,
        is_anchor: !!data.isAnchor,
        has_ambiental_license: !!data.hasAmbientalLicense,
    };
    for (const key in data) {
        parsedData[snakecase(key)] = data[key];
    }
    parsedData.id = parsedData.code;
    // Expose camelCase alias that ShowCustomActions reads directly.
    // The cerealizr deserializer is skipped for project detail responses (kml_simplified guard),
    // so the raw API key is snake_case. Map it explicitly here.
    parsedData.publicationStatus = parsedData.publication_status;
    if (parsedData.end_date) {
        parsedData.end_date = parsedData.end_date.substring(0, 7);
    }
    return parsedData;
};


export const formatSearchResponse = data =>
    data.map(item => {
        return {
            ...item,
            infrastructure: setChoicesField(item.infrastructure, infraestructureChoices),
            financing: setChoicesField(item.financing, financingChoices),
            stage: setChoicesField(item.stage, statusChoices),
            topic: setChoicesField(item.topic, topicChoices),
            type_filter: setChoicesField(item.typeFilter, contentTypeChoices)
        };
    });

const setChoicesField = (strValue, choices) => {
    if (strValue) {
        const value = strValue.split(',');
        let newValue = '';
        value.forEach(v => {
            const ch = choices.filter(c => c.id == v);
            if (ch.length > 0) {
                if (newValue != '') {
                    newValue += ', ';
                }
                newValue += ch[0].name;
            }
        });
        return newValue;
    }
};

// Every metric-value response (list, detail, create, update) goes through this so
// the store holds one shape: FK ids for the form's ReferenceInputs (Edit renders
// the cached record before GET_ONE resolves), plus names for the list columns.
const parseMetricValue = item => {
    const fkId = value => (value && typeof value === 'object' ? value.id : value || null);
    const fkName = value => (value && typeof value === 'object' ? value.name : '');
    return {
        ...item,
        metric: fkId(item.metric),
        pais: fkId(item.pais),
        etapa: fkId(item.etapa),
        subsector: fkId(item.subsector),
        metricName: fkName(item.metric),
        paisName: fkName(item.pais),
        etapaName: fkName(item.etapa),
        subsectorName: fkName(item.subsector),
    };
};

export const parseResourceDetail = {
    [RESOURCES.projects]: parseIirsaProject,
    [RESOURCES['draft-projects']]: parseDraftProject,
    iirsaProjects: parseIirsaProject,
    [RESOURCES.routes]: item => formatRouteResponse([item])[0],
    // [RESOURCES.topics]: data => data
    [RESOURCES['metric-values']]: parseMetricValue,
};

export const formatCreateResource = {
    [RESOURCES.projects]: formatCreateProject,
    iirsaProjects: formatCreateIirsaProject,
    [RESOURCES.routes]: formatCreateRoute,
    [RESOURCES.priorizaciones]: data => ({ name: data.name }),
    [RESOURCES.etapas]: data => {
        // The weight is not sent: it is only set through the bulk etapas/weights endpoint.
        const result = {
            name: data.name,
            nameEn: data.nameEn || '',
            namePt: data.namePt || '',
            // Existing ids go back so the API updates them in place: a Seguimiento may
            // point at one. New rows (added in the form) have none and are created.
            subStages: (data.subStages || []).map(subStage => ({
                ...(subStage.id ? { id: subStage.id } : {}),
                name: subStage.name,
                nameEn: subStage.nameEn || '',
                namePt: subStage.namePt || ''
            }))
        };
        if (data.order != null) result.order = data.order;
        return result;
    },
    [RESOURCES.alcances]: data => ({ name: data.name }),
    [RESOURCES['estados-estudios']]: data => ({ name: data.name }),
    [RESOURCES.estados]: data => ({ name: data.name }),
    [RESOURCES['financiadores-estudios']]: data => ({ name: data.name }),
    [RESOURCES['tipos-proyecto']]: data => ({ name: data.name, subsector: data.subsector }),
    sectores: data => ({ name: data.name }),
    [RESOURCES.subsectores]: data => ({ name: data.name, sector: data.sector }),
    paises: data => ({ name: data.name }),
    [RESOURCES.metrics]: data => ({ name: data.name, unit: data.unit, category: data.category }),
    [RESOURCES['metric-values']]: data => ({
        metric: data.metric,
        pais: data.pais || null,
        etapa: data.etapa || null,
        subsector: data.subsector || null,
        value: data.value,
        detalle: data.detalle || ''
    }),
    // `password` is omitted when blank so an edit that leaves it empty keeps the
    // current one rather than trying to set an empty password.
    [RESOURCES.users]: data => {
        const payload = {
            username: data.username,
            first_name: data.firstName || '',
            last_name: data.lastName || '',
            email: data.email || '',
            is_active: data.isActive !== false,
            role: data.role,
            countries: data.countries || []
        };
        if (data.password) {
            payload.password = data.password;
        }
        return payload;
    },
    [RESOURCES.disclaimers]: data => {
        const result = {
            titleEs: data.titleEs,
            disclaimerEs: data.disclaimerEs,
            titleEn: data.titleEn || '',
            disclaimerEn: data.disclaimerEn || '',
            titlePt: data.titlePt || '',
            disclaimerPt: data.disclaimerPt || ''
        };
        if (data.order != null) result.order = data.order;
        return result;
    }
};

export const formatUpdateResource = {
    [RESOURCES.projects]: formatUpdateProject
};

export const formatResponseData = {
    [RESOURCES.projects]: formatIirsaProjectResponse,
    [RESOURCES['draft-projects']]: formatDraftProjectResponse,
    [RESOURCES.routes]: formatRouteResponse,
    ['searchs']: formatSearchResponse,
    [RESOURCES.priorizaciones]: data => data,
    [RESOURCES.disclaimers]: data => data,
    [RESOURCES.etapas]: data => data,
    [RESOURCES.alcances]: data => data,
    [RESOURCES['estados-estudios']]: data => data,
    [RESOURCES.estados]: data => data,
    [RESOURCES['financiadores-estudios']]: data => data,
    [RESOURCES['tipos-proyecto']]: data => data,
    sectores: data => data,
    [RESOURCES.subsectores]: data => data,
    paises: data => data,
    [RESOURCES.metrics]: data => data,
    [RESOURCES['metric-values']]: data => data.map(parseMetricValue),
    [RESOURCES.countries]: data => data,
    [RESOURCES.layers]: data => data,
    [RESOURCES.users]: data => data
};

export const parseAudit = data => ({
    ...data,
    estimatedEndDate: data.estimatedEndDate ? formatDate(data.estimatedEndDate) : null,
    infrastructure: data.infrastructure
        ? infraestructureChoices.filter(c => c.id == data.infrastructure)[0].name
        : null,
    status: data.status ? statusChoices.filter(c => c.id == data.status)[0].name : null,
    ambientalLicense: data.ambientalLicense != null ? (data.ambientalLicense ? 'Sí' : 'No') : null
});

//Choices
export const topicChoices = [
    {id: 'bim', name: 'BIM'},
    {id: 'blockchain', name: 'Blockchain'},
    {id: 'artificialintelligence', name: 'Inteligencia Artificial'},
    {id: 'iot', name: 'Internet de las Cosas'},
    {id: 'bigdata', name: 'Big Data'}
];

export const contentTypeChoices = [
    {id: 'article', name: 'Artículo'},
    {id: 'infographic', name: 'Infografía'},
    {id: 'publishing', name: 'Publicación'},
    {id: 'video', name: 'Video'}
];
